'use client';

import { type ReactNode, useCallback, useState } from 'react';

import {
  CalendarDays,
  PartyPopper,
  PersonStanding,
  Sparkles,
  Target,
  Trophy,
  UserPlus,
  UserStar,
  Users,
  UsersRound,
  createLucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { UsersThree } from '@kit/ui/users-three-icon';

import { balanceRows } from '../_lib/balance-rows';
import { isGenericClassType } from '../_lib/hooks/use-booking-reducer';
import { BookingChipIcon } from './booking-chip-icon';
import { BookingChoiceButton } from './booking-choice-button';

interface BookingTypeToggleProps {
  value: string;
  onChange: (value: string) => void;
  availableTypes: string[];
  /**
   * When true, prepends a full-width "All Classes" pill (synthetic value `'all'`)
   * and hides `group_classes`. Used for studios where the booking flow is
   * class-only and the per-booking-type axis is meaningless.
   */
  unifiedClassMode?: boolean;
}

/** Synthetic booking-type slug used by the unified class filter. */
export const ALL_CLASSES_BOOKING_TYPE = 'all';

type IconComponent = typeof Users;

// A court seen from above (outer lines, service lines, centre line), drawn
// in the Lucide grid so it matches the other tile glyphs.
const CourtIcon = createLucideIcon('court', [
  [
    'rect',
    {
      x: '2',
      y: '5',
      width: '20',
      height: '14',
      rx: '2',
      key: 'court-outline',
    },
  ],
  ['path', { d: 'M8 5v14', key: 'court-left' }],
  ['path', { d: 'M16 5v14', key: 'court-right' }],
  ['path', { d: 'M8 12h8', key: 'court-centre' }],
]);

// Tabler's users-group (MIT): one person in front of two, for community-style
// types, so it reads differently from the two-person coaching/match glyphs.
const CommunityIcon = createLucideIcon('community', [
  [
    'path',
    { d: 'M10 13a2 2 0 1 0 4 0a2 2 0 0 0-4 0', key: 'community-front-head' },
  ],
  [
    'path',
    {
      d: 'M8 21v-1a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1',
      key: 'community-front-body',
    },
  ],
  [
    'path',
    { d: 'M15 5a2 2 0 1 0 4 0a2 2 0 0 0-4 0', key: 'community-right-head' },
  ],
  ['path', { d: 'M17 10h2a2 2 0 0 1 2 2v1', key: 'community-right-body' }],
  [
    'path',
    { d: 'M5 5a2 2 0 1 0 4 0a2 2 0 0 0-4 0', key: 'community-left-head' },
  ],
  ['path', { d: 'M3 13v-1a2 2 0 0 1 2-2h2', key: 'community-left-body' }],
]);

interface BookingTypeConfig {
  /** `null` renders the label with no icon (e.g. the generic space/court type). */
  icon: string | IconComponent | null;
  labelKey: string;
}

/** Known booking type configs with icons and translation keys */
const KNOWN_BOOKING_TYPE_CONFIG: Record<string, BookingTypeConfig> = {
  court: {
    // Court icon shows for sports tenants ("Court Only"). For tenants that
    // relabel this type away from "court" (e.g. member clubs → "Space Only"),
    // the court glyph would be misleading, so resolveBookingTypeIcon falls
    // back to a name match when the label no longer mentions a court.
    icon: CourtIcon,
    labelKey: 'bookings:courtOnly',
  },
  trainer: {
    icon: UserStar,
    labelKey: 'bookings:coaching',
  },
  ball_machine: {
    icon: Target,
    labelKey: 'bookings:ballMachine',
  },
  group_classes: {
    icon: UsersRound,
    labelKey: 'bookings:groupClasses',
  },
  kids_academy: {
    icon: UsersThree,
    labelKey: 'bookings:kidsAcademy',
  },
  event: {
    icon: CalendarDays,
    labelKey: 'bookings:event',
  },
  tournament: {
    icon: Trophy,
    labelKey: 'bookings:tournament',
  },
};

/** Convert a slug like 'other_custom' to 'Other Custom' */
function slugToLabel(slug: string): string {
  return slug
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Tenant-defined types (community, drop_in, new_match_with_coach…) get a
// people glyph from the words in their slug or label, each kind its own so a
// row of tiles doesn't repeat one icon. The first match wins, so "match with
// coach" reads as a match. Unmatched types fall back to the plain two-person
// glyph.
const TYPE_ICON_KEYWORDS: Array<[keywords: string[], icon: IconComponent]> = [
  [['ladies', 'women', 'womens', 'female', 'girls'], Sparkles],
  [['match', 'matches', 'league', 'americano', 'mexicano'], Users],
  [
    ['coach', 'coaching', 'private', 'lesson', 'lessons', 'trainer', 'pro'],
    UserStar,
  ],
  [['community', 'club', 'members', 'meetup'], CommunityIcon],
  [['social', 'mixer', 'friends'], UsersRound],
  [
    [
      'training',
      'group',
      'team',
      'squad',
      'kids',
      'junior',
      'juniors',
      'academy',
      'children',
    ],
    UsersThree,
  ],
  [['drop', 'dropin', 'walkin'], UserPlus],
  [
    ['clinic', 'clinics', 'workshop', 'camp', 'drill', 'drills'],
    PersonStanding,
  ],
  [['tournament', 'cup', 'championship'], Trophy],
  [['event', 'events', 'party', 'celebration'], PartyPopper],
];

type BookingTypeIcon =
  | { kind: 'image'; src: string }
  | { kind: 'component'; Icon: IconComponent };

function resolveBookingTypeIcon(type: string, label: string): BookingTypeIcon {
  const configured = KNOWN_BOOKING_TYPE_CONFIG[type]?.icon;
  const labelIsCourt =
    label.toLowerCase().includes('court') || label.includes('ملعب');

  if (configured && (type !== 'court' || labelIsCourt)) {
    return typeof configured === 'string'
      ? { kind: 'image', src: configured }
      : { kind: 'component', Icon: configured };
  }

  const words = `${type} ${label}`.toLowerCase().split(/[^a-z]+/);
  const match = TYPE_ICON_KEYWORDS.find(([keywords]) =>
    keywords.some((keyword) => words.includes(keyword)),
  );
  return { kind: 'component', Icon: match?.[1] ?? Users };
}

function BookingTypeIconView({
  icon,
  label,
  size,
}: {
  icon: BookingTypeIcon;
  label: string;
  size: 16 | 24;
}) {
  if (icon.kind === 'image') {
    return <BookingChipIcon src={icon.src} alt={label} size={size} masked />;
  }
  const { Icon } = icon;
  return (
    <Icon
      aria-hidden="true"
      className={size === 24 ? 'size-6 shrink-0' : 'size-4 shrink-0'}
      strokeWidth={size === 24 ? 1.5 : 2}
    />
  );
}

/**
 * Resolve a booking-type slug to the icon element + label. Exported so the
 * single-sport-studio path in booking-form can render a category tab (e.g.
 * "Event") inside SportFilterToggle with the same glyph and label as the
 * tiles below — one source of truth for the slug → icon/label mapping. `t`
 * is passed in so the caller controls translation.
 */
export function resolveBookingTypeChip(
  type: string,
  t: (key: string) => string,
): { icon: ReactNode; label: string } {
  const config = KNOWN_BOOKING_TYPE_CONFIG[type];
  const label = config?.labelKey ? t(config.labelKey) : slugToLabel(type);

  return {
    icon: (
      <BookingTypeIconView
        icon={resolveBookingTypeIcon(type, label)}
        label={label}
        size={16}
      />
    ),
    label,
  };
}

// Narrowest a tile may get before a row wraps; wider from tablet width up.
const TILE_MIN_WIDTH = 80;
const TILE_MIN_WIDTH_WIDE = 128;
const WIDE_ROW = 640;
const TILE_GAP = 8;

export function BookingTypeToggle({
  value,
  onChange,
  availableTypes,
  unifiedClassMode = false,
}: BookingTypeToggleProps) {
  const { t } = useTranslation('bookings');
  // Tiles per row follow the row's measured width, so rows rebalance on
  // rotate/resize. 4 until measured: the common phone width.
  const [maxPerRow, setMaxPerRow] = useState(4);
  const measureRow = useCallback((row: HTMLDivElement | null) => {
    if (!row) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      if (width === 0) return;
      const minTile = width >= WIDE_ROW ? TILE_MIN_WIDTH_WIDE : TILE_MIN_WIDTH;
      setMaxPerRow(
        Math.max(1, Math.floor((width + TILE_GAP) / (minTile + TILE_GAP))),
      );
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  // In unified-class mode, drop the generic class types (group_classes,
  // semi_private) from the per-template row — their sessions get rolled into
  // "All Classes" (rendered by SportFilterToggle). A studio whose standard
  // class is semi_private must fold the same way group_classes does, or it
  // strands every class under a standalone "Semi Private" pill.
  const visibleTypes = unifiedClassMode
    ? availableTypes.filter((type) => !isGenericClassType(type))
    : availableTypes;

  // In unified mode the "All Classes" pill lives inside SportFilterToggle so
  // it shares one container with the sport row. If nothing remains here, this
  // toggle has nothing to render.
  if (visibleTypes.length === 0) {
    return null;
  }

  // Balanced rows (see balanceRows): the fewest rows that fit, split as
  // evenly as possible, each stretched to the full width. Same selected style
  // as every other booking choice (BookingChoiceButton).
  const rowSizes = balanceRows(visibleTypes.length, maxPerRow);
  const rows = rowSizes.map((size, index) => {
    const start = rowSizes.slice(0, index).reduce((sum, n) => sum + n, 0);
    return visibleTypes.slice(start, start + size);
  });

  return (
    <div ref={measureRow} className="space-y-2" data-test="booking-type">
      {rows.map((rowTypes) => (
        <div
          key={rowTypes.join('|')}
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${rowTypes.length}, minmax(0, 1fr))`,
          }}
        >
          {rowTypes.map((type) => {
            const config = KNOWN_BOOKING_TYPE_CONFIG[type];
            const label = config?.labelKey
              ? t(config.labelKey)
              : slugToLabel(type);

            return (
              <BookingChoiceButton
                key={type}
                selected={value === type}
                onClick={() => value !== type && onChange(type)}
                className="h-auto min-h-[4.5rem] w-full flex-col justify-center gap-1.5 px-1.5 py-2 text-center leading-tight whitespace-normal"
              >
                <span className="text-primary">
                  <BookingTypeIconView
                    icon={resolveBookingTypeIcon(type, label)}
                    label={label}
                    size={24}
                  />
                </span>
                <span>{label}</span>
              </BookingChoiceButton>
            );
          })}
        </div>
      ))}
    </div>
  );
}
